import { createContext, useContext, useState, ReactNode, useCallback, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { ABSOLUTE_PATHS } from '@/core/constants';

interface RockHallTVContextType {
  channels: RockHallTvChannel[];
  activeChannel: RockHallTvChannel | null;
  activeVideo: RockHallTvVideo | null;
  showModal: boolean;
  setActiveChannel: (channel: RockHallTvChannel | null) => void;
  setActiveVideo: (video: RockHallTvVideo | null) => void;
  setShowModal: (show: boolean) => void;
  updateRHTVRoute: (params?: { channelSlug?: string; videoSlug?: string }) => void;
}

const RockHallTVContext = createContext<RockHallTVContextType | undefined>(undefined);

interface RockHallTVProviderProps {
  children: ReactNode;
  channels?: RockHallTvChannel[];
  defaultChannel?: RockHallTvChannel | null;
  defaultVideo?: RockHallTvVideo | null;
  isPreview?: boolean;
}

export const RockHallTVProvider: React.FC<RockHallTVProviderProps> = ({
  children,
  channels = [],
  defaultChannel = null,
  defaultVideo = null,
  isPreview = false,
}) => {
  const [activeChannel, setActiveChannelState] = useState<RockHallTvChannel | null>(defaultChannel);
  const [activeVideo, setActiveVideoState] = useState<RockHallTvVideo | null>(defaultVideo);
  const [showModal, setShowModal] = useState<boolean>(false);
  const router = useRouter();
  const pathRef = useRef<string>(router.asPath);

  const updateRHTVRoute = useCallback((params?: { channelSlug: string; videoSlug: string }) => {
    if (isPreview) return;
    const { channelSlug, videoSlug } = params || {};
    let newPath: string = ABSOLUTE_PATHS.RRTV;
    if (channelSlug && videoSlug) newPath += `c/${channelSlug}/v/${videoSlug}`;

    // Preserve existing query parameters
    const currentQuery = new URLSearchParams(window.location.search);
    const queryString = currentQuery.toString();
    if (queryString) newPath += `?${queryString}`;

    // Check both pathRef and current router path to prevent unnecessary replaces
    if (pathRef.current === newPath || router.asPath === newPath) return;

    // Update pathRef before routing to prevent duplicate updates
    pathRef.current = newPath;

    router.replace(newPath, undefined, { shallow: true, scroll: false });
  }, [router, isPreview]);

  // Wrapper for setActiveChannel that doesn't update route (handled elsewhere)
  const setActiveChannel = useCallback((channel: RockHallTvChannel | null) => {
    setActiveChannelState(channel);
  }, []);

  // Wrapper for setActiveVideo that also updates the route
  const setActiveVideo = useCallback((video: RockHallTvVideo | null) => {
    setActiveVideoState(video);

    // Update route when video changes (if we have both channel and video)
    if (activeChannel && video) {
      updateRHTVRoute({ channelSlug: activeChannel.slug, videoSlug: video.slug });
    }
  }, [activeChannel, updateRHTVRoute]);

  useEffect(() => {
    // Only track route changes that are RHTV-related (start with /rrtv/)
    if (!router.asPath.startsWith(ABSOLUTE_PATHS.RRTV)) return;
    if (router.asPath === pathRef.current) return;
    pathRef.current = router.asPath;
  }, [router.asPath]);

  const value: RockHallTVContextType = {
    channels,
    activeChannel,
    activeVideo,
    showModal,
    setActiveChannel,
    setActiveVideo,
    setShowModal,
    updateRHTVRoute,
  };

  return (
    <RockHallTVContext.Provider value={value}>
      {children}
    </RockHallTVContext.Provider>
  );
};

export const useRockHallTV = (): RockHallTVContextType => {
  const context = useContext(RockHallTVContext);
  if (context === undefined) {
    throw new Error('useRockHallTV must be used within a RockHallTVProvider');
  }
  return context;
};
